import { useCallback } from 'react';
import { useMarkDismissed } from '@gonfalon/rest-api/hooks/useMarkDismissed';
import { getMemberDismissalsQuery } from '@gonfalon/rest-api/queries/memberDismissals';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * Generic show-once primitive for one-time announcements. Suspends until the member's
 * server-persisted dismissals are loaded (a fetch error propagates to the nearest error
 * boundary), then reports whether `key` has been dismissed and exposes an idempotent
 * `markSeen`. The underlying mutation seeds the query cache, so the announcement won't
 * re-trigger in-session.
 */
export function useOneTimeAnnouncement(key: string) {
  const { data } = useSuspenseQuery(getMemberDismissalsQuery());
  const { mutate } = useMarkDismissed();

  const dismissed = Boolean(data[key]);

  const markSeen = useCallback(() => {
    if (!dismissed) {
      mutate({ dismissalKey: key });
    }
  }, [dismissed, key, mutate]);

  return { dismissed, markSeen };
}
